import {
  anchorISO,
  listDirection,
  listDirectionExplicit,
  forwardTotal,
  backwardTotal,
  notPerformedTotal,
  totalsLoaded,
  timeFromYear,
  timeToYear,
  meta,
} from "../state/store";
import { openAbout } from "./AboutDialog";
import { openDownload } from "./DownloadDialog";

/** The ONE place a result count appears. Sticky above the list, it doubles as the
 *  temporal direction selector when the window includes today: "Upcoming" reads forward
 *  from today, "Past" reads backward — one time axis, two reading directions. Until the
 *  first real totals arrive it shows meta.json's static totals, dimmed, instead of a
 *  zero or a spinner (the cold-start honesty rule: never claim "0" while loading). */
export function CountBar() {
  const anchored = anchorISO.value !== null;
  const direction = listDirection.value;
  const loaded = totalsLoaded.value;

  let forward: string;
  let backward: string;
  if (loaded) {
    forward = forwardTotal.value.toLocaleString();
    backward = backwardTotal.value.toLocaleString();
  } else if (meta.value) {
    // Static stand-ins for the default (unfiltered, full-window) view: upcoming = the
    // exporter's from-today count; past = the rest of the live corpus + the archive.
    const m = meta.value;
    const upcoming = m.upcoming_count ?? m.concert_count;
    forward = upcoming.toLocaleString();
    backward = (
      Math.max(0, m.concert_count - upcoming) + (m.historical?.concert_count ?? 0)
    ).toLocaleString();
  } else {
    // Nothing has loaded yet — never claim "0" while loading.
    forward = "…";
    backward = "…";
  }

  function jumpToFilters() {
    document.getElementById("filters")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div class={`count-bar${loaded ? "" : " is-provisional"}`}>
      {anchored ? (
        <div class="dir-toggle" role="group" aria-label="Reading direction">
          <button
            class={`dir-btn ${direction === "forward" ? "active" : ""}`}
            type="button"
            aria-pressed={direction === "forward"}
            onClick={() => {
              listDirection.value = "forward";
              listDirectionExplicit.value = true;
            }}
          >
            Upcoming <strong>{forward}</strong>
          </button>
          <button
            class={`dir-btn dir-btn-past ${direction === "backward" ? "active" : ""}`}
            type="button"
            aria-pressed={direction === "backward"}
            onClick={() => {
              listDirection.value = "backward";
              listDirectionExplicit.value = true;
            }}
          >
            Past <strong>{backward}</strong>
          </button>
        </div>
      ) : (
        <span class="concert-count">
          <strong>{forward}</strong>
          {` events, ${timeFromYear.value}–${timeToYear.value}`}
        </span>
      )}
      {loaded && notPerformedTotal.value > 0 && (
        <span
          class="count-bar-note"
          title="Listings the presenter marked cancelled or postponed stay in the list, badged, but are not counted here or on the map."
        >
          +{notPerformedTotal.value.toLocaleString()} cancelled
        </span>
      )}
      <span class="count-bar-actions">
        {/* The footer is unreachable in practice (infinite list) — the sticky bar is
            where the About affordance actually gets seen. */}
        <button class="concert-list-top-btn" onClick={openAbout} type="button">
          About
        </button>
        <button
          class="concert-list-top-btn"
          onClick={openDownload}
          type="button"
          title="Download these concerts as a spreadsheet (CSV)"
        >
          Download CSV
        </button>
        <button class="concert-list-top-btn" onClick={jumpToFilters} type="button">
          Filters ↑
        </button>
      </span>
    </div>
  );
}
